import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ChevronDown } from 'lucide-react'
import type {
  ActionBarNode,
  AppNode,
  BadgeRowNode,
  CalloutNode,
  ChartNode,
  EmptyStateNode,
  FilterBarNode,
  KPIGridNode,
  ListNode,
  SectionNode,
  StatNode,
  TableNode,
} from '@/services/api/miniappTypes'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/utils/cn'
import { agentMauriceChartPalette } from '@/openui/chartPalette'
import { chartRows, pieRows } from '@/openui/library'
import { useOSColorMode } from '@/openui/openuiTheme'

type MetricNode = AppNode & {
  type: 'metric'
  label?: unknown
  value?: string | number
  delta?: string | number
  trend?: 'up' | 'down' | 'neutral'
  caption?: string
}

type StepsNode = AppNode & {
  type: 'steps'
  items?: Array<{ title: string; description?: string; status?: 'done' | 'current' | 'todo' }>
}

type AccordionNode = AppNode & {
  type: 'accordion'
  items?: Array<{ title?: unknown; children?: AppNode[] }>
}

interface MiniAppNodeRendererProps {
  node?: AppNode | null
  onEvent: (eventId: string, payload?: Record<string, any>) => void
  onOpenForm: (formId: string, submitEvent?: string) => void
}

function renderStat(node: StatNode) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-sm">
      <span className="text-muted-foreground">{node.label}</span>
      <span className="text-base font-semibold text-foreground">{node.value}</span>
    </div>
  )
}

function renderKPIGrid(node: KPIGridNode) {
  if (!Array.isArray(node.items)) return null

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {node.items.map((item, index) => {
        const toneClassName =
          item.tone === 'success'
            ? 'border-success/30 bg-success-soft'
            : item.tone === 'warning'
              ? 'border-warning/30 bg-warning-soft'
              : item.tone === 'error'
                ? 'border-error/30 bg-error-soft'
                : 'border-border bg-card'

        return (
          <div
            key={`${item.label}-${index}`}
            className={`rounded-xl border px-4 py-3 ${toneClassName}`}
          >
            <p className="text-[13px] font-semibold text-foreground">
              {item.label}
            </p>
            <p className="mt-2 text-xl font-semibold text-foreground">{item.value}</p>
          </div>
        )
      })}
    </div>
  )
}

function renderList(
  node: ListNode,
  renderer: (node: AppNode) => ReactNode,
  onEvent: MiniAppNodeRendererProps['onEvent'],
) {
  if (!Array.isArray(node.source)) return null

  return (
    <div className="space-y-2">
      {node.source.map((item, index) => {
        const clickable = Boolean(node.on_item_click)
        const content = node.item_template
          ? renderer(node.item_template)
          : typeof item === 'string'
            ? item
            : JSON.stringify(item)

        return (
          <div
            key={index}
            className={cn(
              'rounded-lg border border-border bg-card p-3 text-sm transition',
              clickable && 'cursor-pointer hover:border-input hover:bg-card',
            )}
            onClick={
              node.on_item_click
                ? () => onEvent(node.on_item_click, { item, index })
                : undefined
            }
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
          >
            {content}
          </div>
        )
      })}
    </div>
  )
}

function renderTabs(
  node: AppNode & { items?: string[]; active?: string; on_tab_click?: string },
  onEvent: MiniAppNodeRendererProps['onEvent'],
) {
  if (!Array.isArray(node.items) || typeof node.on_tab_click !== 'string') return null

  return (
    <div className="flex flex-wrap gap-2">
      {node.items.map((item, index) => {
        const active = node.active === item
        return (
          <Button
            key={`${item}-${index}`}
            type="button"
            variant={active ? 'secondary' : 'outline'}
            className={active ? 'border-info/30 bg-info-soft text-info' : undefined}
            onClick={() => onEvent(node.on_tab_click as string, { item, index })}
          >
            {item}
          </Button>
        )
      })}
    </div>
  )
}

function renderFilterBar(
  node: FilterBarNode,
  onEvent: MiniAppNodeRendererProps['onEvent'],
) {
  if (!Array.isArray(node.filters) || node.filters.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-card p-2">
      {node.filters.map((filter, index) => (
        <Button
          key={`${filter.label}-${filter.value}-${index}`}
          type="button"
          variant={filter.active ? 'secondary' : 'outline'}
          className={filter.active ? 'border-info/30 bg-info-soft text-info' : undefined}
          onClick={() =>
            onEvent(filter.on_click, {
              item: filter.value,
              label: filter.label,
            })
          }
        >
          <span className="font-medium">{filter.label}</span>
          <span className="ml-2">{filter.value}</span>
        </Button>
      ))}
    </div>
  )
}

function renderActionBar(
  node: ActionBarNode,
  onEvent: MiniAppNodeRendererProps['onEvent'],
  onOpenForm: MiniAppNodeRendererProps['onOpenForm'],
) {
  if (!Array.isArray(node.actions) || node.actions.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-card p-2">
      {node.actions.map((action, index) => {
        const clickableEvent = typeof action.on_click === 'string'
        const clickableForm = typeof action.form_id === 'string'
        if (!clickableEvent && !clickableForm) return null

        return (
          <Button
            key={`${action.label}-${index}`}
            type="button"
            variant={
              action.variant === 'secondary'
                ? 'outline'
                : clickableForm && !clickableEvent
                  ? 'outline'
                  : 'default'
            }
            onClick={() => {
              if (clickableForm) {
                onOpenForm(
                  action.form_id as string,
                  typeof action.submit_event === 'string' ? action.submit_event : undefined,
                )
                return
              }
              onEvent(action.on_click as string)
            }}
          >
            {action.label}
          </Button>
        )
      })}
    </div>
  )
}

function renderBadgeRow(node: BadgeRowNode) {
  if (!Array.isArray(node.items) || node.items.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {node.items.map((item, index) => {
        const className =
          item.tone === 'success'
            ? 'border-success/30 bg-success-soft text-success'
            : item.tone === 'warning'
              ? 'border-warning/30 bg-warning-soft text-warning'
              : item.tone === 'error'
                ? 'border-error/30 bg-error-soft text-error'
                : 'border-border bg-card text-foreground'

        return (
          <Badge
            key={`${item.label}-${index}`}
            variant="secondary"
            className={`rounded-full border px-3 py-1 text-xs font-medium ${className}`}
          >
            <span className="text-muted-foreground">{item.label}</span>
            <span className="ml-2">{item.value}</span>
          </Badge>
        )
      })}
    </div>
  )
}

function renderGroup(
  node: { children?: AppNode[]; layout?: 'column' | 'row' },
  renderer: (node: AppNode) => ReactNode,
) {
  const layoutClassName =
    node.layout === 'row' ? 'flex flex-wrap gap-3' : 'flex flex-col gap-3'

  return (
    <div className={layoutClassName}>
      {node.children?.map((child, index) => (
        <div key={`${child.type ?? 'node'}-${index}`} className="min-w-0 flex-1">
          {renderer(child)}
        </div>
      ))}
    </div>
  )
}

function renderCallout(node: CalloutNode) {
  const toneClassName =
    node.tone === 'success'
      ? 'border-success/30 bg-success-soft text-success'
      : node.tone === 'warning'
        ? 'border-warning/30 bg-warning-soft text-warning'
        : node.tone === 'error'
          ? 'border-error/30 bg-error-soft text-error'
          : node.tone === 'neutral'
            ? 'border-border bg-muted text-foreground'
            : 'border-info/30 bg-info-soft text-info'

  return (
    <div className={cn('rounded-xl border px-4 py-3', toneClassName)}>
      {node.title ? <p className="text-sm font-semibold">{node.title}</p> : null}
      <p className={cn('text-sm', node.title ? 'mt-1' : '')}>{node.value}</p>
    </div>
  )
}

function renderEmptyState(
  node: EmptyStateNode,
  onEvent: MiniAppNodeRendererProps['onEvent'],
) {
  const hasAction = typeof node.action_label === 'string' && typeof node.on_click === 'string'

  return (
    <div className="rounded-xl border border-dashed border-border bg-card px-5 py-6 text-center">
      <p className="text-sm font-semibold text-foreground">{node.title}</p>
      <p className="mt-2 text-sm text-muted-foreground">{node.description}</p>
      {hasAction ? (
        <Button
          type="button"
          variant="outline"
          className="mt-4"
          onClick={() => onEvent(node.on_click as string)}
        >
          {node.action_label}
        </Button>
      ) : null}
    </div>
  )
}

function renderTable(
  node: TableNode & { on_row_click?: string; column_labels?: Record<string, string> },
  onEvent: MiniAppNodeRendererProps['onEvent'],
) {
  if (!Array.isArray(node.columns) || !Array.isArray(node.rows)) return null

  return (
    <div className="overflow-x-auto rounded-card border border-border bg-card">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-card">
          <tr>
            {node.columns.map((column) => (
              <th
                key={column}
                className="h-10 px-3 text-left text-[13px] font-semibold text-foreground"
              >
                {node.column_labels?.[column] ?? column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border [&>tr:nth-child(even)]:bg-muted [&>tr]:h-10">
          {node.rows.map((row, rowIndex) => {
            const cells = Array.isArray(row)
              ? row
              : node.columns.map((column) => (row as Record<string, any>)[column])

            return (
              <tr
                key={rowIndex}
                className={typeof node.on_row_click === 'string' ? 'cursor-pointer transition hover:bg-primary-soft' : undefined}
                onClick={
                  typeof node.on_row_click === 'string'
                    ? () =>
                        onEvent(node.on_row_click, {
                          item: row,
                          index: rowIndex,
                        })
                    : undefined
                }
              >
                {cells.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`} className="px-3 py-2 text-foreground">
                    {cell == null ? '—' : String(cell)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

type ChartPoint = { label: string; value: number }

const CHART_TYPES = new Set(['bar', 'line', 'area', 'pie', 'horizontal_bar'])
const CHART_WIDTH = 320
const CHART_HEIGHT = 160

function chartPoints(node: ChartNode): ChartPoint[] {
  type ChartRecord = Record<string, string | number | boolean | null>
  const records = (node.data as unknown[]).filter(
    (row) => Boolean(row) && !Array.isArray(row) && typeof row === 'object',
  ) as ChartRecord[]
  return chartRows(records, node.x_key, node.y_key).map((row) => ({
    label: String(row[node.x_key] || '—'),
    value: Number(row[node.y_key]),
  }))
}

function pointCoordinates(points: ChartPoint[], maxValue: number) {
  return points.map((point, index) => ({
    x: points.length > 1 ? (index / (points.length - 1)) * CHART_WIDTH : CHART_WIDTH / 2,
    y: maxValue > 0 ? CHART_HEIGHT - (point.value / maxValue) * (CHART_HEIGHT - 20) : CHART_HEIGHT - 10,
  }))
}

function ChartLabels({ points }: { points: ChartPoint[] }) {
  return (
    <div className="mt-3 flex justify-between gap-2 text-[11px] text-muted-foreground">
      {points.map((point, index) => (
        <span key={`${point.label}-${index}`} className="truncate">
          {point.label}
        </span>
      ))}
    </div>
  )
}

function renderLineOrArea(node: ChartNode, points: ChartPoint[], color: string) {
  const maxValue = points.reduce((max, point) => Math.max(max, point.value), 0)
  const coordinates = pointCoordinates(points, maxValue)
  const polyline = coordinates.map(({ x, y }) => `${x},${y}`).join(' ')
  const area =
    coordinates.length > 0
      ? `${coordinates[0].x},${CHART_HEIGHT} ${polyline} ${coordinates[coordinates.length - 1].x},${CHART_HEIGHT}`
      : ''

  return (
    <>
      <svg
        className="mt-4 h-40 w-full"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={node.title ?? undefined}
      >
        {node.chart_type === 'area' ? (
          <polygon points={area} fill={color} fillOpacity={0.2} stroke="none" />
        ) : null}
        <polyline fill="none" stroke={color} strokeWidth="3" points={polyline} />
        {coordinates.map(({ x, y }, index) => (
          <circle key={`${points[index].label}-${index}`} cx={x} cy={y} r="4" fill={color} />
        ))}
      </svg>
      <ChartLabels points={points} />
    </>
  )
}

function renderBars(points: ChartPoint[], color: string) {
  const maxValue = points.reduce((max, point) => Math.max(max, point.value), 0)
  return (
    <div className="mt-4 flex items-end gap-3">
      {points.map((point, index) => {
        const height = maxValue > 0 ? Math.max((point.value / maxValue) * 140, 8) : 8
        return (
          <div key={`${point.label}-${index}`} className="flex min-w-0 flex-1 flex-col items-center gap-2">
            <span className="text-xs font-medium text-foreground">{point.value}</span>
            <div className="w-full rounded-t-md" style={{ height: `${height}px`, backgroundColor: color }} />
            <span className="truncate text-[11px] text-muted-foreground">{point.label}</span>
          </div>
        )
      })}
    </div>
  )
}

function renderHorizontalBars(points: ChartPoint[], color: string) {
  const maxValue = points.reduce((max, point) => Math.max(max, point.value), 0)
  return (
    <div className="mt-4 space-y-2">
      {points.map((point, index) => (
        <div key={`${point.label}-${index}`} className="grid grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-3 text-xs">
          <span className="truncate text-muted-foreground">{point.label}</span>
          <div className="h-3 rounded-full bg-muted">
            <div
              className="h-3 rounded-full"
              style={{
                width: `${maxValue > 0 ? Math.max((point.value / maxValue) * 100, 2) : 2}%`,
                backgroundColor: color,
              }}
            />
          </div>
          <span className="font-medium text-foreground">{point.value}</span>
        </div>
      ))}
    </div>
  )
}

function pieSlicePath(startAngle: number, endAngle: number) {
  const radius = 70
  const center = 80
  const point = (angle: number) => [
    center + radius * Math.sin(angle),
    center - radius * Math.cos(angle),
  ]
  const [x1, y1] = point(startAngle)
  const [x2, y2] = point(endAngle)
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0
  return `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
}

function renderPie(node: ChartNode, points: ChartPoint[], palette: string[], otherLabel: string) {
  const rows = pieRows(
    points.map((point) => ({ label: point.label, value: point.value })),
    'label',
    'value',
    palette.length,
    otherLabel,
  ).map((row) => ({ label: String(row.label), value: Number(row.value) }))
  const total = rows.reduce((sum, row) => sum + Math.max(row.value, 0), 0)
  let angle = 0

  return (
    <div className="mt-4 flex flex-wrap items-center gap-4">
      <svg className="h-40 w-40" viewBox="0 0 160 160" role="img" aria-label={node.title ?? undefined}>
        {rows.map((row, index) => {
          const share = total > 0 ? Math.max(row.value, 0) / total : 0
          const start = angle
          angle += share * 2 * Math.PI
          const color = palette[index % palette.length]
          return share >= 0.9999 ? (
            <circle key={`${row.label}-${index}`} cx="80" cy="80" r="70" fill={color} />
          ) : share > 0 ? (
            <path key={`${row.label}-${index}`} d={pieSlicePath(start, angle)} fill={color} />
          ) : null
        })}
      </svg>
      <ul className="space-y-1 text-xs">
        {rows.map((row, index) => (
          <li key={`${row.label}-${index}`} className="flex items-center gap-2 text-foreground">
            <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: palette[index % palette.length] }} />
            <span>{row.label}</span>
            <span className="font-medium">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Fallback of the OpenUI ChartBlock: same chart types, same AgentMaurice
// palette (chartPalette of @agent-maurice/ui), series i on slot i.
function renderChart(node: ChartNode, palette: string[], t: TFunction) {
  if (
    !CHART_TYPES.has(node.chart_type) ||
    typeof node.x_key !== 'string' ||
    typeof node.y_key !== 'string' ||
    !Array.isArray(node.data)
  ) return null

  const points = chartPoints(node)
  const color = palette[0]
  let body: ReactNode
  if (points.length === 0) {
    body = <p className="mt-4 text-center text-sm text-muted-foreground">{t('common.miniappNoData')}</p>
  } else if (node.chart_type === 'line' || node.chart_type === 'area') {
    body = renderLineOrArea(node, points, color)
  } else if (node.chart_type === 'pie') {
    body = renderPie(node, points, palette, t('common.miniappOther'))
  } else if (node.chart_type === 'horizontal_bar') {
    body = renderHorizontalBars(points, color)
  } else {
    body = renderBars(points, color)
  }

  return (
    <figure className="rounded-xl border border-border bg-card p-4" data-chart-type={node.chart_type}>
      {node.title ? <figcaption className="text-sm font-semibold text-foreground">{node.title}</figcaption> : null}
      {body}
    </figure>
  )
}

const TREND_ARROW = { up: '↑', down: '↓', neutral: '' } as const

function renderMetric(node: MetricNode) {
  if (typeof node.label !== 'string' || node.value === undefined) return null
  const trend = node.trend === 'up' || node.trend === 'down' ? node.trend : 'neutral'
  const delta = node.delta === undefined || node.delta === '' ? '' : `${TREND_ARROW[trend]} ${node.delta}`.trim()
  const subtext = [delta, node.caption].filter(Boolean).join(' · ')

  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3" data-trend={trend}>
      <p className="text-[13px] font-semibold text-muted-foreground">{node.label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{String(node.value)}</p>
      {subtext ? <p className="mt-1 text-xs text-muted-foreground">{subtext}</p> : null}
    </div>
  )
}

const STEP_STATUS_KEY = {
  done: 'common.miniappStepDone',
  current: 'common.miniappStepCurrent',
  todo: 'common.miniappStepTodo',
} as const

function renderSteps(node: StepsNode, t: TFunction) {
  if (!Array.isArray(node.items) || node.items.length === 0) return null

  return (
    <ol className="space-y-3">
      {node.items.map((item, index) => {
        const status = item.status === 'done' || item.status === 'current' ? item.status : 'todo'
        return (
          <li key={`${item.title}-${index}`} className="flex gap-3" data-status={status}>
            <span
              aria-hidden="true"
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                status === 'current' ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground',
              )}
            >
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {item.title}
                <span
                  className={cn(
                    'ml-2 text-xs',
                    status === 'done' ? 'text-success' : status === 'current' ? 'font-semibold text-link' : 'text-muted-foreground',
                  )}
                >
                  {t(STEP_STATUS_KEY[status])}
                </span>
              </p>
              {item.description ? <p className="mt-1 text-xs text-muted-foreground">{item.description}</p> : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

// Fallback of the OpenUI AccordionBlock: several panels may be open, the
// first one starts open. Native <details> keeps keyboard and screen reader
// support without extra state.
function renderAccordion(
  node: AccordionNode,
  renderer: (node: AppNode) => ReactNode,
) {
  if (!Array.isArray(node.items) || node.items.length === 0) return null

  return (
    <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
      {node.items.map((item, index) => (
        <details
          key={`${String(item?.title ?? '')}-${index}`}
          className="group"
          open={index === 0}
          data-accordion-item={index}
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
            <span className="min-w-0 truncate">{String(item?.title ?? '')}</span>
            <ChevronDown
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none"
            />
          </summary>
          <div className="px-4 pb-4 pt-1">
            {renderGroup({ children: Array.isArray(item?.children) ? item.children : [] }, renderer)}
          </div>
        </details>
      ))}
    </div>
  )
}

function renderSection(
  node: SectionNode,
  renderer: (node: AppNode) => ReactNode,
) {
  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4">
      {node.title ? (
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-foreground">{node.title}</h4>
          {'subtitle' in node && typeof node.subtitle === 'string' && node.subtitle ? (
            <p className="mt-1 text-xs text-muted-foreground">{node.subtitle}</p>
          ) : null}
        </div>
      ) : null}
      {renderGroup(node, renderer)}
    </div>
  )
}

function renderCard(
  node: SectionNode,
  renderer: (node: AppNode) => ReactNode,
) {
  return (
    <Card className="border-border bg-card">
      {node.title ? (
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{node.title}</CardTitle>
          {'subtitle' in node && typeof node.subtitle === 'string' && node.subtitle ? (
            <p className="text-sm text-muted-foreground">{node.subtitle}</p>
          ) : null}
        </CardHeader>
      ) : null}
      <CardContent className={node.title ? undefined : 'pt-6'}>
        {renderGroup(node, renderer)}
      </CardContent>
    </Card>
  )
}

export default function MiniAppNodeRenderer({
  node,
  onEvent,
  onOpenForm,
}: MiniAppNodeRendererProps) {
  const { t } = useTranslation()
  const palette = agentMauriceChartPalette[useOSColorMode()]
  const renderNode = (current: AppNode): ReactNode => {
    if (!current || typeof current !== 'object') return null

    if (!('type' in current) || typeof current.type !== 'string') {
      if (Array.isArray((current as any).children)) {
        return renderGroup(current as any, renderNode)
      }
      return null
    }

    switch (current.type) {
      case 'text': {
        const node = current as AppNode & { value?: unknown }
        return <p className="text-sm text-foreground">{String(node.value ?? '')}</p>
      }
      case 'button': {
        const node = current as AppNode & { label?: unknown; on_click?: unknown }
        if (typeof node.on_click !== 'string') return null
        const eventId = node.on_click
        return (
          <Button
            type="button"
            variant="secondary"
            onClick={() => onEvent(eventId)}
          >
            {String(node.label ?? '')}
          </Button>
        )
      }
      case 'stat':
        if (typeof current.label !== 'string' || current.value === undefined) return null
        return renderStat(current as StatNode)
      case 'kpi_grid':
        return renderKPIGrid(current as KPIGridNode)
      case 'list':
        return renderList(current as ListNode, renderNode, onEvent)
      case 'tabs':
        return renderTabs(current as AppNode & { items?: string[]; active?: string; on_tab_click?: string }, onEvent)
      case 'filter_bar':
        return renderFilterBar(current as FilterBarNode, onEvent)
      case 'action_bar':
        return renderActionBar(current as ActionBarNode, onEvent, onOpenForm)
      case 'badge_row':
        return renderBadgeRow(current as BadgeRowNode)
      case 'callout':
        return renderCallout(current as CalloutNode)
      case 'empty_state':
        return renderEmptyState(current as EmptyStateNode, onEvent)
      case 'chart':
        return renderChart(current as ChartNode, palette, t)
      case 'metric':
        return renderMetric(current as MetricNode)
      case 'steps':
        return renderSteps(current as StepsNode, t)
      case 'accordion':
        return renderAccordion(current as AccordionNode, renderNode)
      case 'separator':
        return <div className="my-2 border-t border-border" role="separator" />
      case 'table':
        return renderTable(current as TableNode & { on_row_click?: string }, onEvent)
      case 'section':
        return renderSection(current as SectionNode, renderNode)
      case 'card':
        return renderCard(current as SectionNode, renderNode)
      case 'form_link': {
        const node = current as AppNode & {
          label?: unknown
          form_id?: unknown
          submit_event?: unknown
        }
        if (typeof node.form_id !== 'string') return null
        const formId = node.form_id
        return (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              onOpenForm(
                formId,
                typeof node.submit_event === 'string' ? node.submit_event : undefined,
              )
            }
          >
            {String(node.label ?? '')}
          </Button>
        )
      }
      default:
        if (Array.isArray((current as any).children)) {
          return renderGroup(current as any, renderNode)
        }
        return null
    }
  }

  if (!node) return null
  return <>{renderNode(node)}</>
}
